import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generateInsights } from 'api/aiModalApi';
import selectors from 'store/selectors';
import {
  setAIChatContextId,
  setAIChatHistory,
  setAIChatLoading,
  setAIChatError,
  setAIChatState,
} from 'store/actions/aiChatActions';

interface ChatMessage {
  chatId: string;
  message: string;
  type: 'user' | 'assistant';
  createdAt: Date;
}

function useAIChat() {
  const dispatch = useDispatch();
  const chatHistory = useSelector(selectors.getAIChatHistory);
  const isLoadingHistory = useSelector(selectors.getAIChatLoading);
  const error = useSelector(selectors.getAIChatError);
  const chatContextId = useSelector(selectors.getAIChatContextId);

  // Open chat panel and generate/load context
  const openChatPanel = useCallback(
    async (contextType: string, contextData: any) => {
      dispatch(setAIChatLoading(true));
      dispatch(setAIChatError(null));

      try {
        // Generate new context for chat using same contextType but different contextId
        const response = await generateInsights({
          contextType,
          contextData,
          includeChatHistory: true,
        });

        if (response?.data?.contextId) {
          dispatch(setAIChatContextId(response.data.contextId));
        }

        if (response?.data?.chatHistory) {
          dispatch(setAIChatHistory(response.data.chatHistory));
        }
      } catch (error) {
        console.error('Failed to generate chat context:', error);
        dispatch(setAIChatError('Failed to generate chat context'));
      } finally {
        dispatch(setAIChatLoading(false));
      }
    },
    [dispatch],
  );

  // Close chat panel
  const closeChatPanel = useCallback(() => {
    // Clear all chat state when closing
    dispatch(setAIChatContextId(null));
    dispatch(setAIChatHistory([]));
    dispatch(setAIChatError(null));
    dispatch(setAIChatLoading(false));
    dispatch(setAIChatState(false));
  }, [dispatch]);

  // Send message
  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || !chatContextId) return;

      dispatch(setAIChatError(null));

      try {
        const response = await generateInsights({
          contextId: chatContextId,
          contextData: { userMessage: message },
          includeChatHistory: true,
        });

        if (response?.data?.chatHistory) {
          dispatch(setAIChatHistory(response.data.chatHistory));
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        dispatch(setAIChatError('Failed to send message'));
        throw error;
      }
    },
    [chatContextId, dispatch],
  );

  // Clear chat history
  const clearChatHistory = useCallback(() => {
    dispatch(setAIChatHistory([]));
    dispatch(setAIChatContextId(null));
    dispatch(setAIChatError(null));
  }, [dispatch]);

  return {
    chatHistory,
    isLoadingHistory,
    error,
    chatContextId,
    openChatPanel,
    closeChatPanel,
    sendMessage,
    clearChatHistory,
  };
}

export default useAIChat;
